// SEQ-180 phase 2, task P17: the unit-aware metric selector (plan 7.2).
//
// Grouped by registry section, each option carrying a unit chip and a role
// chip, with a search that matches the display name OR the key. Operational
// metrics are never hidden: putting them in the same picker as everything
// else is what SEQ-182 AC07 asked for.
//
// Names render as plain JSX text, never dangerouslySetInnerHTML, which is a
// recorded follow-up rule for registry display names (SEQ-182 plan section
// 13, item 3): a display name is user-entered.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { PNL_UNIT_CHIP } from "../../lib/pnl/pnlWidgetSchema";
import { resolveMetricRole, resolveMetricUnit } from "../../lib/pnl/pnlAnalytics";
import type { PnlMetricOption } from "../../features/pnlAnalytics/metricOption";
import { groupMetricsForSelector, METRIC_ROLE_CHIP } from "../../features/pnlAnalytics/widgetEditorState";
import "./PnlWidgetEditor.css";

export interface PnlMetricSelectorProps {
  label: string;
  value: string;
  metrics: PnlMetricOption[];
  onChange: (metricKey: string) => void;
  /** Rendered when the field has none, e.g. "Optional. Adds a second series." */
  hint?: string;
  error?: string | null;
  /** Adds a "None" option, used by the optional secondary metric field. */
  allowNone?: boolean;
}

export function PnlMetricSelector({ label, value, metrics, onChange, hint, error, allowNone }: PnlMetricSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const listboxId = `${fieldId}-listbox`;

  const groups = useMemo(() => groupMetricsForSelector(metrics, query), [metrics, query]);
  const selected = metrics.find((m) => m.metricKey === value);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape, true);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape, true);
    };
  }, [open]);

  const choose = (metricKey: string) => {
    onChange(metricKey);
    setOpen(false);
    setQuery("");
  };

  const triggerText = value === "" ? "None" : (selected?.displayName ?? value);

  return (
    <div className="pnl-field" ref={rootRef} style={{ position: "relative" }}>
      <label className="form-label" htmlFor={fieldId}>
        {label}
      </label>
      {/* Design audit, MEDIUM: the ARIA here was invalid. The search input sat
          INSIDE role="listbox", where only role="option" children are allowed,
          which makes a screen reader announce an option count that includes a
          text field. The popover is now a plain container: the trigger is a
          combobox pointing at the listbox through aria-controls, the search
          box sits outside the listbox, and each registry section is a
          role="group" with its own name so "Labour, 6 options" is announced
          rather than one flat list of forty. */}
      <button
        type="button"
        id={fieldId}
        className="form-input pnl-metric-trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-describedby={error ? errorId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span className="pnl-metric-option-name">{triggerText}</span>
          {selected && <MetricChips metric={selected} />}
        </span>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }} />
      </button>

      {open && (
        <div className="pnl-metric-popover">
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px 8px" }}>
            <Search size={14} style={{ color: "var(--text-secondary)" }} aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              className="form-input"
              style={{ padding: "5px 8px", fontSize: 12 }}
              placeholder="Search by name or key"
              aria-label={`Search ${label.toLowerCase()}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div role="listbox" id={listboxId} aria-label={label}>
            {allowNone && (
              <button
                type="button"
                role="option"
                aria-selected={value === ""}
                className="pnl-metric-option"
                onClick={() => choose("")}
              >
                <span className="pnl-metric-option-name">None</span>
              </button>
            )}

            {groups.map((group) => (
              <div key={group.section} role="group" aria-label={group.label}>
                <div className="pnl-metric-group-label" aria-hidden="true">{group.label}</div>
                {group.options.map((metric) => (
                  <button
                    key={metric.metricKey}
                    type="button"
                    role="option"
                    aria-selected={metric.metricKey === value}
                    className="pnl-metric-option"
                    onClick={() => choose(metric.metricKey)}
                  >
                    <span className="pnl-metric-option-name">{metric.displayName}</span>
                    <MetricChips metric={metric} />
                  </button>
                ))}
              </div>
            ))}
          </div>

          {groups.length === 0 && (
            <div role="status" style={{ padding: 10, fontSize: 12, color: "var(--text-secondary)" }}>
              No metric matches that search.
            </div>
          )}
        </div>
      )}

      {error ? (
        <p className="pnl-field-error" id={errorId}>
          {error}
        </p>
      ) : (
        hint && <p className="pnl-field-hint">{hint}</p>
      )}
    </div>
  );
}

function MetricChips({ metric }: { metric: PnlMetricOption }) {
  const role = METRIC_ROLE_CHIP[resolveMetricRole(metric)] ?? "Financial";
  return (
    <>
      <span className="pnl-chip">{PNL_UNIT_CHIP[resolveMetricUnit(metric)]}</span>
      <span className="pnl-chip" data-role={role}>
        {role}
      </span>
    </>
  );
}
